import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import LocationField from './LocationField';

jest.mock('components/styleguide/icon/Icon', () => {
  return {
    __esModule: true,
    default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
  };
});

jest.mock('components/form/location_input/location_input', () => {
  return {
    __esModule: true,
    default: ({ placeholder, required, handleLocationChange, handleReset, ...props }: any) => (
      <div data-testid="location-input" className={props.class}>
        <input
          id="location-filter"
          placeholder={placeholder}
          required={required}
          onChange={(e) => {
            if (e.target.value) {
              handleLocationChange?.({ address_components: [] });
            }
          }}
        />
        <button data-testid="reset-btn" onClick={() => handleReset?.()} type="button">Reset</button>
      </div>
    ),
  };
});

describe('LocationField', () => {
  it('renders the location input', () => {
    render(<LocationField />);
    expect(screen.getByTestId('location-input')).toBeInTheDocument();
  });

  it('renders label associated with input via htmlFor', () => {
    render(<LocationField id="location-filter" label="Location" />);
    const label = screen.getByText('Location');
    expect(label.tagName).toBe('LABEL');
    expect(label).toHaveAttribute('for', 'location-filter');
  });

  it('renders required asterisk hidden from screen readers', () => {
    render(<LocationField label="Location" required />);
    const asterisk = screen.getByText('*');
    expect(asterisk).toHaveAttribute('aria-hidden', 'true');
    expect(asterisk.tagName).toBe('SPAN');
  });

  it('renders placeholder', () => {
    render(<LocationField placeholder="Search city..." />);
    expect(screen.getByPlaceholderText('Search city...')).toBeInTheDocument();
  });

  it('renders hint text', () => {
    render(<LocationField hint="Start typing a city name" />);
    expect(screen.getByText('Start typing a city name')).toBeInTheDocument();
  });

  it('renders error message with alert role', () => {
    render(<LocationField error="Location is required" />);
    expect(screen.getByRole('alert')).toHaveTextContent('Location is required');
  });

  it('does not render alert role for hint', () => {
    render(<LocationField hint="Just a hint" />);
    expect(screen.queryByRole('alert')).not.toBeInTheDocument();
  });

  it('renders error icon when error is present', () => {
    const { container } = render(<LocationField error="Error" />);
    expect(container.querySelector('[data-icon="triangleAlert"]')).toBeInTheDocument();
  });

  it('renders info icon for hint', () => {
    const { container } = render(<LocationField hint="Hint" />);
    expect(container.querySelector('[data-icon="info"]')).toBeInTheDocument();
  });

  describe('accessibility', () => {
    it('associates error message via id', () => {
      render(<LocationField id="loc" error="Required" />);
      expect(document.getElementById('loc-message')).toHaveTextContent('Required');
    });

    it('associates hint message via id', () => {
      render(<LocationField id="loc" hint="Choose a city" />);
      expect(document.getElementById('loc-message')).toHaveTextContent('Choose a city');
    });

    it('does not render message container when no hint or error', () => {
      render(<LocationField id="loc" />);
      expect(document.getElementById('loc-message')).toBeNull();
    });

    it('renders error with alert role', () => {
      render(<LocationField error="Required" />);
      expect(screen.getByRole('alert')).toHaveTextContent('Required');
    });

    it('does not render alert role for hint', () => {
      render(<LocationField hint="Just a hint" />);
      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });
  });

  describe('error clearing', () => {
    it('clears error on location change', () => {
      render(<LocationField error="Required" />);
      expect(screen.getByRole('alert')).toBeInTheDocument();

      const input = screen.getByPlaceholderText('E.g. Lisbon');
      fireEvent.change(input, { target: { value: 'Lisbon' } });

      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });

    it('shows validation error on reset when required', () => {
      render(<LocationField required defaultValidationMessage="Location required" />);
      const resetBtn = screen.getByTestId('reset-btn');
      fireEvent.click(resetBtn);

      expect(screen.getByRole('alert')).toHaveTextContent('Location required');
    });

    it('syncs displayError when error prop changes', () => {
      const { rerender } = render(<LocationField error="First" />);
      expect(screen.getByText('First')).toBeInTheDocument();

      rerender(<LocationField error="Second" />);
      expect(screen.getByText('Second')).toBeInTheDocument();
    });

    it('clears displayError when error prop is removed', () => {
      const { rerender } = render(<LocationField error="Error" />);
      expect(screen.getByText('Error')).toBeInTheDocument();

      rerender(<LocationField />);
      expect(screen.queryByText('Error')).not.toBeInTheDocument();
    });

    it('clears error on text input', () => {
      render(<LocationField error="Required" />);
      expect(screen.getByRole('alert')).toBeInTheDocument();

      const input = screen.getByPlaceholderText('E.g. Lisbon');
      fireEvent.input(input, { target: { value: 'L' } });

      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });
  });
});
